import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const client = import.meta.env.VITE_ADSENSE_CLIENT;
const placeholder = import.meta.env.VITE_ADSENSE_PLACEHOLDER === 'true';
// Automated browsers (bots, e2e) never request real ads: they count as filled.
const simulated = placeholder || (typeof navigator !== 'undefined' && navigator.webdriver === true);

// Whether ads show at all is now a runtime KV config toggle (allowAds / allowRecommendAds),
// gated by the callers (BottomContent, HomeImages). This component only validates the
// AdSense identifiers and renders.
export default function AdsenseAd({ slotId, style, responsive = true, onRendered }) {
    const ready = client && !client.includes('X') && slotId && !slotId.includes('X');
    const insRef = useRef(null);

    useEffect(() => {
        if (!ready) return;
        // dev placeholder has no real ad; report filled so frequency logic is testable.
        if (simulated) {
            onRendered?.('filled');
            return;
        }
        try {
            (window.adsbygoogle = window.adsbygoogle || []).push({});
        } catch (e) {}
        const ins = insRef.current;
        if (!ins) return;
        // AdSense sets data-ad-status to 'filled' / 'unfilled' once resolved.
        const report = () => {
            const status = ins.getAttribute('data-ad-status');
            if (status === 'filled' || status === 'unfilled') {
                onRendered?.(status);
                return true;
            }
            return false;
        };
        if (report()) return;
        const observer = new MutationObserver(() => {
            if (report()) observer.disconnect();
        });
        observer.observe(ins, { attributes: true, attributeFilter: ['data-ad-status'] });
        return () => observer.disconnect();
    }, [ready]);

    if (!ready) return null;

    if (placeholder) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 1,
                    minHeight: '100px',
                    ...style,
                }}>
                <Typography variant='caption' color='text.disabled'>
                    AD
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ textAlign: 'center', py: 1, minHeight: '100px', ...style }}>
            <ins
                ref={insRef}
                className='adsbygoogle'
                style={{ display: 'block', minHeight: '100px' }}
                data-ad-client={client}
                data-ad-slot={slotId}
                {...(responsive && {
                    'data-ad-format': 'auto',
                    'data-full-width-responsive': 'true',
                })}
            />
        </Box>
    );
}
